import React from 'react';
import { Target, Eye, Award, Users, Lightbulb, Shield, IndianRupee, HeartHandshake, Wrench, Gauge, Cog, Microscope } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';

const fadeInUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="about-page">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold">About Namoh Toolings</h1>
            <p className="text-lg text-white/90 tracking-widest uppercase">We Listen We Innovate</p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Who We Are</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Namoh Toolings is a leading manufacturer and supplier of precision cutting tools and tooling solutions. With years of expertise in the industry, we deliver innovative products that enhance productivity and manufacturing excellence.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Award, label: 'Experience', value: '20+ Years' },
                { icon: Users, label: 'Clients', value: '500+ Happy Clients' },
                { icon: Target, label: 'Products', value: '20,000+ Products' },
                { icon: Eye, label: 'Professionals', value: '50+ Professionals' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border-0 bg-white hover:shadow-xl transition-all duration-300 group">
                    <CardContent className="p-6 text-center space-y-3">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2E3A6F]/10 group-hover:bg-[#2E3A6F] transition-colors duration-300">
                        <stat.icon className="text-[#2E3A6F] group-hover:text-white transition-colors" size={28} />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                        <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Target,
                title: 'Our Mission',
                content: 'To provide world-class tooling solutions that empower manufacturers to achieve operational excellence and superior quality.',
                color: 'bg-blue-500'
              },
              {
                icon: Eye,
                title: 'Our Vision',
                content: 'To be the most trusted partner in precision tooling, known for innovation, quality, and customer-centric approach.',
                color: 'bg-purple-500'
              }
            ].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="border-0 bg-gradient-to-br from-[#f8f9fb] to-white hover:shadow-xl transition-all duration-300 h-full">
                  <CardContent className="p-8 space-y-4">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl ${section.color} bg-opacity-10`}>
                      <section.icon className="text-[#2E3A6F]" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{section.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{section.content}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values - Cutting Tools Related */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto space-y-12">
            <motion.div
              className="text-center space-y-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900">Our Core Values</h2>
              <p className="text-lg text-gray-600">Precision-driven principles that define our craftsmanship</p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                {
                  icon: Microscope,
                  title: 'Precision First',
                  description: 'Every cutting tool is crafted to micron-level accuracy. We maintain the tightest tolerances to ensure flawless machining performance and surface finish quality.',
                  color: 'from-[#2E3A6F] to-[#4a5a9f]'
                },
                {
                  icon: Wrench,
                  title: 'Application Engineering',
                  description: 'We don\'t just sell tools - we engineer complete machining solutions. Our team recommends the right grade, geometry, and cutting parameters for your specific application.',
                  color: 'from-[#2E3A6F] to-[#4a5a9f]'
                },
                {
                  icon: Gauge,
                  title: 'Performance Optimization',
                  description: 'Maximizing tool life and cutting speed is our obsession. We continuously optimize feeds, speeds, and insert geometries to reduce your cost per component.',
                  color: 'from-[#2E3A6F] to-[#4a5a9f]'
                },
                {
                  icon: Cog,
                  title: 'Machining Excellence',
                  description: 'From turning and milling to grooving and threading - we provide comprehensive tooling solutions that cover every CNC machining operation with superior results.',
                  color: 'from-[#2E3A6F] to-[#4a5a9f]'
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ y: -5, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="border-0 bg-white hover:shadow-xl transition-all duration-500 group h-full overflow-hidden" data-testid={`core-value-${index}`}>
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#2E3A6F] to-[#4a5a9f] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top" />
                    <CardContent className="p-6 flex items-start space-x-4 relative">
                      <motion.div
                        className={`w-16 h-16 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center shadow-lg flex-shrink-0`}
                        whileHover={{ rotate: 15, scale: 1.1 }}
                        transition={{ duration: 0.4, type: "spring" }}
                      >
                        <value.icon className="text-white" size={30} />
                      </motion.div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-[#2E3A6F] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-64 h-64 border border-white/20 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 border border-white/20 rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-12 space-y-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold">Why Choose</h2>
              <div className="flex items-center justify-center gap-3">
                <img src="/favicon.png" alt="Namoh Toolings" className="h-12 w-12 object-contain" />
                <span className="text-2xl sm:text-3xl font-bold">Namoh Toolings</span>
              </div>
              <p className="text-white/80 tracking-widest uppercase text-sm">We Listen We Innovate</p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                {
                  icon: Lightbulb,
                  title: 'Innovative & Dedicated Engineers',
                  description: 'Our innovative and dedicated engineers embody our tagline: "WE LISTEN WE INNOVATE"'
                },
                {
                  icon: IndianRupee,
                  title: 'We Value Your Money',
                  description: 'We take pride in our ability to optimize cost per component and save valuable time for our customers.'
                },
                {
                  icon: HeartHandshake,
                  title: 'We Value Your Concerns',
                  description: 'We go beyond merely providing tools. We are committed to supporting our customers with comprehensive machining programming solutions.'
                }
              ].map((item, index) => (
                <motion.div key={index} variants={fadeInUp}>
                  <motion.div
                    className="text-center space-y-4 p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-500 h-full"
                    whileHover={{ y: -8, scale: 1.03 }}
                    transition={{ duration: 0.3 }}
                    data-testid={`about-why-choose-${index}`}
                  >
                    <motion.div
                      className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/10"
                      whileHover={{ rotate: 15, scale: 1.15 }}
                      transition={{ duration: 0.4, type: "spring" }}
                    >
                      <item.icon size={32} />
                    </motion.div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed">{item.description}</p>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
